import { useState } from 'react'

import {
  Box,
  Button,
  Chevron,
  Divider,
  Icon,
  Prose,
  Text,
  Touchable,
} from '@factorialco/deprecated-design-system'
import {
  PerformanceEmployeePerformanceInsightsSummaryFilter,
  PerformanceInsightsGenerateAggregationsDocument,
} from 'generated/resources/graphql'
import { useToggle } from 'usehooks-ts'

import useFilters from 'modules/core/components/DataTable/hooks/useFilters'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { EngagementSignalTag } from 'modules/performance_analytics/components/EngagementSignalTag'

export function AggregationsSidepanel() {
  const [open, setOpen] = useState(false)
  const { getFiltersVariables } = useFilters()
  const dataTableFilters =
    // eslint-disable-next-line no-type-assertion/no-type-assertion -- DataTable types are not very precise
    (getFiltersVariables().filter as PerformanceEmployeePerformanceInsightsSummaryFilter) ?? {}

  const [mutate, { data, loading }] = useMutation(PerformanceInsightsGenerateAggregationsDocument, {
    extractorFn: (mutationData) =>
      mutationData.performanceMutations.generateForFiltersPerformanceReviewAggregation,
  })

  const reviewAggregation =
    data?.performanceMutations.generateForFiltersPerformanceReviewAggregation.reviewAggregation

  const generateAggregation = async () => {
    const response = await mutate({
      variables: {
        employeeIds: dataTableFilters.employeeId?.in,
        managerIds: dataTableFilters.managerId?.in,
        scoreRange: dataTableFilters.scoreRange?.in,
        teamIds: dataTableFilters.teamId?.in,
        gridStatus: dataTableFilters.gridStatus?.in,
        tenureDate: dataTableFilters.tenureDate?.in,
        potentialScoreRange: dataTableFilters.potentialScoreRange?.in,
      },
    })

    return handleResult(response, () => undefined)
  }

  return (
    <Box>
      <Box flexDirection="row" gap="s8">
        {reviewAggregation ? (
          <Button.Split
            label={i18n.t('performance_process.analytics.aggregations.show_result')}
            type="primary"
            onClick={() => setOpen(true)}
          >
            <Button.Split.Option iconName="ai" onClick={generateAggregation}>
              {i18n.t('performance_process.analytics.aggregations.regenerate')}
            </Button.Split.Option>
          </Button.Split>
        ) : (
          <Button iconName="ai" onClick={generateAggregation} loading={loading}>
            {loading
              ? i18n.t('performance_process.analytics.aggregations.generating')
              : i18n.t('performance_process.analytics.aggregations.generate')}
          </Button>
        )}
      </Box>

      <Sidepanel
        open={open}
        onClose={() => setOpen(false)}
        title={
          <Box flexDirection="row" alignItems="center" gap="s8">
            <Icon.Medium icon="ai" color="purple1000" />
            <Text.Normal size="300" weight="semibold">
              {i18n.t('performance_process.analytics.aggregations.sidepanel_title')}
            </Text.Normal>
          </Box>
        }
        closeLabel={i18n.t('common.close')}
        width="wide"
      >
        {reviewAggregation && (
          <Box gap="s24">
            {(reviewAggregation.topics || []).map((topic, index) => (
              <Box
                key={topic.topicName}
                gap="s12"
                // border={{ all: { color: 'grey400', width: 's1', style: 'solid' } }}
                // borderRadius={{ all: 'abs006' }}
              >
                <Text.Normal size="300" weight="semibold">
                  {topic.topicName}
                </Text.Normal>
                <Box gap="s8" flexDirection="row" flexWrap="wrap">
                  {(topic.signals || []).map((signal) => (
                    <EngagementSignalTag key={signal.name} engagementSignal={signal} />
                  ))}
                </Box>
                <TopicSummaryCollapsable summary={topic.summary} />
                {index !== reviewAggregation.topics.length - 1 && <Divider />}
              </Box>
            ))}
          </Box>
        )}
      </Sidepanel>
    </Box>
  )
}

function TopicSummaryCollapsable(props: { summary: string }) {
  const [showSummary, toggleShowSummary] = useToggle(false)

  return (
    <Box gap="s16">
      <Touchable onClick={toggleShowSummary}>
        <Box flexDirection="row" alignItems="center" gap="s8" flexGrow>
          <Text.Normal size="200" color="grey800" weight="medium">
            {showSummary
              ? i18n.t('performance_process.analytics.aggregations.hide_summary')
              : i18n.t('performance_process.analytics.aggregations.show_summary')}
          </Text.Normal>
          <Chevron type="secondary" direction={showSummary ? 'up' : 'down'} />
        </Box>
      </Touchable>
      {showSummary && <Prose markdown>{props.summary}</Prose>}
    </Box>
  )
}
